//dependencies
import React from "react";
import { z } from "zod";

//components
import { FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../../../../components/Inputs/SelectEmployee";

import { fetchEmployees } from "../../../../../../../api/services/defs/employees";

const ReviewsOfMyTeamFilterSchema = z.object({
	subject: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.nullable()
		.optional(),
	status: z.string().optional(),
	easyAddStatus: z.string().optional(),
});

export type ReviewsOfMyTeamFilters = z.infer<typeof ReviewsOfMyTeamFilterSchema>;

export const ReviewsOfMyTeam360ReviewsOptions = [
	{ value: "easyAddEnabled", label: "360 Enabled" },
	{ value: "awaitingApproval", label: "Awaiting Approval" },
];

export const ReviewsOfMyTeamStatusOptions = [
	{ value: "active", label: "Active" },
	{ value: "complete", label: "Completed" },
	{ value: "scheduled", label: "Scheduled" },
];

interface ReviewsOfMyTeamFiltersPanelProps {
	filters?: ReviewsOfMyTeamFilters;
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: ReviewsOfMyTeamFilters) => void;
}

export const ReviewsOfMyTeamFiltersPanel = ({
	filters,
	isOpen,
	onClose,
	onApplyFilters,
}: ReviewsOfMyTeamFiltersPanelProps) => {
	const handleSubmit = ({
		value,
	}: {
		value: ReviewsOfMyTeamFilters;
		resetForm: () => void;
		setError: (field: keyof ReviewsOfMyTeamFilters, error: string) => void;
	}) => {
		const formattedState = { ...value };

		// Remove empty, null, or undefined fields
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key];
			if (filterValue === null || filterValue === undefined || filterValue === "") {
				delete formattedState[key];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	const fetchDirectReports = async (searchTerm: string) => {
		const employees = await fetchEmployees({
			searchTerm,
			limitToDirectReports: true,
		});
		return employees.users.map((user) => ({
			id: user._id,
			name: user.name,
		}));
	};

	const defaultValues: ReviewsOfMyTeamFilters = {
		subject: filters?.subject
			? "id" in filters.subject
				? filters.subject
				: {
						id: (filters.subject as { value: string }).value,
						name: (filters.subject as { label: string }).label,
					}
			: null,
		status: filters?.status,
		easyAddStatus: filters?.easyAddStatus,
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={ReviewsOfMyTeamFilterSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<{ id: string; name: string } | null>
					id="subject"
					label="Subject"
					input={(field) => (
						<SingleSelectEmployee
							placeholder="Make a selection"
							value={field.value ?? undefined}
							onChange={(employeeId, employeeName) => {
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								);
							}}
							fetchEmployees={fetchDirectReports}
						/>
					)}
				/>

				<FormRadioButtons id="status" label="Status" options={ReviewsOfMyTeamStatusOptions} />

				<FormRadioButtons id="easyAddStatus" label="360 Reviews" options={ReviewsOfMyTeam360ReviewsOptions} />
			</div>
		</PanelFilters>
	);
};

export default ReviewsOfMyTeamFiltersPanel;
